import React, { useCallback, useState, createContext, useContext } from 'react';
import { XIcon, CheckCircle, XCircle, AlertTriangle, Info } from 'lucide-react';

const TOAST_STYLES: Record<ToastType, { cls: string; Icon: React.ComponentType<{ className?: string }> }> = {
  success: { cls: 'bg-ok-bg border-ok-border text-ok-fg', Icon: CheckCircle },
  error:   { cls: 'bg-danger-bg border-danger-border text-danger-fg', Icon: XCircle },
  warning: { cls: 'bg-warn-bg border-warn-border text-warn-fg', Icon: AlertTriangle },
  info:    { cls: 'bg-info-bg border-info-border text-info-fg', Icon: Info },
};
type ToastType = 'success' | 'error' | 'warning' | 'info';
interface ToastAction {
  label: string;
  onClick: () => void;
}
interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
  action?: ToastAction;
}
interface ToastContextType {
  toasts: Toast[];
  addToast: (message: string, type: ToastType, duration?: number, action?: ToastAction) => void;
  removeToast: (id: string) => void;
}
const ToastContext = createContext<ToastContextType | undefined>(undefined);
export const ToastProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const addToast = useCallback((message: string, type: ToastType, duration = 5000, action?: ToastAction) => {
    const id = Math.random().toString(36).substring(2, 9);
    const newToast: Toast = {
      id,
      message,
      type,
      duration,
      action
    };
    setToasts(prevToasts => [...prevToasts, newToast]);
    // Auto-remove toast after duration
    if (duration !== Infinity) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }
  }, []);
  const removeToast = useCallback((id: string) => {
    setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
  }, []);
  const value = {
    toasts,
    addToast,
    removeToast
  };
  return <ToastContext.Provider value={value}>
      {children}
      {/* Toast container */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2 max-w-md">
        {toasts.map(toast => {
          const { cls, Icon } = TOAST_STYLES[toast.type] || TOAST_STYLES.info;
          return (
            <div key={toast.id} className={`flex items-start gap-3 p-4 rounded-lg border shadow-e2 ${cls}`}>
              <Icon className="h-[18px] w-[18px] flex-shrink-0 mt-0.5" />
              <p className="text-body-sm font-medium">{toast.message}</p>
              {toast.action && (
                <button
                  onClick={() => { toast.action!.onClick(); removeToast(toast.id); }}
                  className="ml-1 text-body-sm font-semibold underline underline-offset-2 hover:opacity-80 flex-shrink-0"
                >
                  {toast.action.label}
                </button>
              )}
              <button onClick={() => removeToast(toast.id)} className="ml-auto text-ink-400 hover:text-ink-700" aria-label="Dismiss">
                <XIcon className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>;
};
export const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};